// src/components/InviteUserModal.jsx
// Modal for creating invitation codes with seat validation

import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";

import { useSubscription } from "../context/SubscriptionContext";
import { teamService } from "../api/teamService";

/**
 * InviteUserModal - Modal for creating invitation codes
 *
 * Features:
 * - Two-step flow: form -> code display
 * - Email input (optional - if empty, anyone can use the code)
 * - Role selection (admin/agent)
 * - Copy code to clipboard
 * - Seat availability check
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSuccess - Success callback
 */
export default function InviteUserModal({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const { seats, refreshSeats } = useSubscription();

  // Form state
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Created invite state
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  const canInvite = seats?.can_add_members ?? false;

  const resetState = () => {
    setEmail("");
    setRole("agent");
    setError("");
    setCreatedInvite(null);
    setCopied(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCreateInvite = async () => {
    setError("");

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Check seats availability
    if (!canInvite) {
      setError("No seats available. Please upgrade your plan to add more members.");
      return;
    }

    setLoading(true);

    try {
      const invite = await teamService.createInvite({
        email: email.trim() || undefined,
        role,
        max_uses: 1,
        expires_in_days: 7,
      });

      setCreatedInvite(invite);
      await refreshSeats();
    } catch (err) {
      if (err.message.includes("seat limit")) {
        setError("No seats available. Please upgrade your plan to add more members.");
      } else if (err.message.includes("already")) {
        setError("This email already has a pending invite or is already a member.");
      } else {
        setError(err.message || "Failed to create invitation. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (!createdInvite?.code) return;

    try {
      await navigator.clipboard.writeText(createdInvite.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDone = () => {
    onSuccess?.();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonAddRoundedIcon color="primary" />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {createdInvite ? "Invitation Created" : "Invite Team Member"}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {/* Show created code */}
        {createdInvite ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share this invite code with the new team member:
            </Typography>

            {/* Code display */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  fontFamily: "monospace",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "primary.main",
                }}
              >
                {createdInvite.code}
              </Box>
              <Tooltip title={copied ? "Copied!" : "Copy code"}>
                <IconButton
                  onClick={handleCopyCode}
                  color={copied ? "success" : "default"}
                  sx={{
                    bgcolor: copied
                      ? alpha(theme.palette.success.main, 0.1)
                      : alpha(theme.palette.action.selected, 0.1),
                  }}
                >
                  {copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* Invite details */}
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              {createdInvite.email && (
                <Chip
                  icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
                  label={createdInvite.email}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`Role: ${createdInvite.role}`}
                size="small"
                variant="outlined"
                sx={{ textTransform: "capitalize" }}
              />
              <Chip
                label="Expires in 7 days"
                size="small"
                variant="outlined"
                color="warning"
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
              The new member can register at{" "}
              <strong>agentblayna.com/register</strong> using this code.
              {createdInvite.email && (
                <> Only {createdInvite.email} can use this code.</>
              )}
            </Typography>
          </Box>
        ) : (
          /* Show form */
          <>
            {/* Seats warning if almost full */}
            {seats && seats.seats_available === 1 && (
              <Alert
                severity="warning"
                icon={<WarningAmberRoundedIcon />}
                sx={{ mb: 3 }}
              >
                This is your last available seat. You'll need to upgrade your
                plan to add more members after this.
              </Alert>
            )}

            {/* No seats available warning */}
            {!canInvite && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500}>
                  No seats available
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Your plan allows up to {seats?.seat_limit} team members.
                  Please upgrade to add more.
                </Typography>
              </Alert>
            )}

            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              {/* Email input */}
              <TextField
                fullWidth
                label="Email Address (Optional)"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                disabled={loading || !canInvite}
                helperText="Leave empty to create a code anyone can use"
              />

              {/* Role select */}
              <FormControl fullWidth disabled={loading || !canInvite}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="agent">
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        Agent
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can manage their own clients and deals
                      </Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        Admin
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full access including team management and billing
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Info text */}
              <Typography variant="caption" color="text.secondary">
                The invite code will expire in 7 days and can only be used once.
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {createdInvite ? (
          <Button variant="contained" onClick={handleDone} fullWidth>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateInvite}
              disabled={loading || !canInvite}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {loading ? "Creating..." : "Create Invite Code"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
